import React, { useState, useImperativeHandle } from 'react';
import 'styles/CriticalitySlider.css';
  
/**
 * CriticalitySlider — a colour‑changing slider for indicating how critical a task is.
 *
 * Props
 *  ────────────────────────────────────────────────────────────────────
 *  min          number   lowest selectable value (default 0)
 *  max          number   highest selectable value (default 100)
 *  step         number   increment between values  (default 1)
 *  defaultValue number   initial value            (default 0)
 *  onChange     fn       called with (newValue) whenever the user moves the slider
 *
 * Usage example
 *  ────────────────────────────────────────────────────────────────────
 *  import CriticalitySlider from './CriticalitySlider.jsx';
 *
 *  const [criticality, setCriticality] = useState(0);
 *
 *  <CriticalitySlider
 *    defaultValue={25}
 *    onChange={setCriticality}
 *    ref={sliderRef}
 *  />
 *
 *  //  you can also read the value imperatively via `sliderRef.current.value`
 */

const CriticalitySlider = ({ min = 0, max = 100, step = 1, defaultValue = 0, onChange, ref }) => {
  
  const [value, setValue] = useState(defaultValue);

  // percentage in the range [0,100]
  const pct = ((value - min) / (max - min)) * 100;

  // hue maps 0 → green(120°) to 100 → red(0°)
  const hue = 120 - pct * 1.2;
  const colour = `hsl(${hue}, 100%, 45%)`;

  // expose current value to the parent (read‑only)
  // and allow the parent to reset the slider to 0
  useImperativeHandle(ref, () => ({
    value,
    reset() { setValue(0); },
  }), [value]);

  const handleChange = (e) => {
    const newVal = Number(e.target.value);
    setValue(newVal);
    if (onChange) onChange(newVal);
  };

  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={handleChange}
      className="criticality-slider"
      /*
        * We paint the track up to the current thumb position with the same
        * colour as the thumb. For browsers that respect CSS variables inside
        * pseudo‑elements we supply both the gradient (for the track) and the
        * solid fill (for the thumb) as custom properties.
        */
      style={{
        '--track-bg': `linear-gradient(90deg, ${colour} 0%, ${colour} ${pct}%, #d1d5db ${pct}%, #d1d5db 100%)`,
        '--thumb-colour': colour,
      }}
    />
  );
}
  
export default CriticalitySlider;